/**
 * API Error Utilities
 *
 * Provides helpers to classify API errors and extract user-friendly messages.
 * The Axios error interceptor in useApi.ts already enriches error.message with
 * the server-provided message, so we leverage that here.
 */
import axios from 'axios';
import i18next from 'i18next';

/**
 * Known technical patterns in error messages mapped to i18n keys.
 */
const TECHNICAL_MESSAGE_MAP: Array<{ pattern: RegExp; key: string }> = [
  // Cognito NotAuthorizedException / UserNotFoundException surfaced by the
  // onboarding login endpoint — raw English text, not an i18n key, so it
  // must be mapped here or it renders untranslated in non-English locales.
  { pattern: /^Invalid email or password$/i, key: 'auth.loginError' },
  // Raw Cognito-derived messages from handleCognitoAuthError (auth.service.ts) —
  // same rationale as above: these come straight from the API body, not a key.
  { pattern: /Sign-in was blocked by a security policy/i, key: 'auth.securityPolicyBlocked' },
  { pattern: /User account is not confirmed/i, key: 'auth.userNotConfirmed' },
  { pattern: /Password reset is required/i, key: 'auth.passwordResetRequired' },
  { pattern: /Login rejected by a pre-authentication policy/i, key: 'auth.lambdaValidationFailed' },
  { pattern: /Too many login attempts/i, key: 'auth.tooManyLoginAttempts' },
  { pattern: /verification code is incorrect/i, key: 'auth.invalidMfaCode' },
  { pattern: /Your session has expired\. Please log in again/i, key: 'auth.sessionExpired' },
  { pattern: /Too many failed attempts\. Please wait and try again/i, key: 'auth.mfaLocked' },
  { pattern: /Failed to activate authenticator app/i, key: 'auth.totpSetupFailed' },
  { pattern: /No authenticator app configured/i, key: 'auth.mfaNotConfigured' },
  { pattern: /P2025/i, key: 'apiErrors.recordNotFound' },
  { pattern: /ECONNREFUSED/i, key: 'apiErrors.connectionFailed' },
  { pattern: /ETIMEDOUT|ECONNABORTED/i, key: 'apiErrors.requestTimeout' },
  { pattern: /network\s?error/i, key: 'apiErrors.networkError' },
  { pattern: /Route\s+(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS):/i, key: 'apiErrors.routeNotFound' },
  { pattern: /Cannot permanently delete.*unit\(s\) in stock/i, key: 'apiErrors.inventoryProductHasStock' },
  { pattern: /Cannot permanently delete.*OPENED/i, key: 'apiErrors.inventoryProductHasOpenedUnits' },
  // MRN duplicate — from pre-flight assertMrnUnique or race-condition P2002 catch
  { pattern: /mrn.*already in use|already in use.*mrn|this mrn is already/i, key: 'apiErrors.duplicateMrn' },
  // Duplicate inventory lot (same batch + location for a product)
  { pattern: /inventory lot.*already exists/i, key: 'apiErrors.duplicateInventoryLot' },
  // Duplicate receipt/receivable number
  { pattern: /receipt number.*already exists/i, key: 'apiErrors.duplicateReceiptNumber' },
  // Prisma P2002 unique constraint on receipt_number (from bulk create 500 path)
  { pattern: /unique constraint.*receipt_number|receipt_number.*unique constraint|P2002.*receipt_number/i, key: 'apiErrors.duplicateReceiptNumber' },
  // Duplicate donor code during provider registration
  { pattern: /donor code.*already exists/i, key: 'apiErrors.duplicateDonorCode' },
  // Duplicate mother-infant link
  { pattern: /mother.infant link.*already exists|link.*person.*patient.*already exists/i, key: 'apiErrors.duplicateMotherInfantLink' },
  // Re-dosing submitted a preparation-level barcode instead of a split-level barcode
  // (reDosing.service.ts create()) — raw English text from the API, not an i18n key.
  { pattern: /Re-dosing requires a split-level barcode/i, key: 'feedAdmin.reDoseDialog.toastInvalidContainerFormat' },
  // Duplicate donor preference (patient-donor assignment)
  { pattern: /donor preference.*already exists/i, key: 'apiErrors.duplicateDonorPreference' },
  // Pool already has batches — its bottles are locked and it cannot be deleted (pools.service.ts)
  { pattern: /cannot be added to or removed from pool .* because batches have already been created/i, key: 'apiErrors.poolHasBatchesBottlesLocked' },
  { pattern: /^Pool .* cannot be deleted because batches have already been created/i, key: 'apiErrors.poolHasBatchesCannotDelete' },
  // Duplicate user email — Cognito UsernameExistsException surfaced by the onboarding service
  { pattern: /A user with this email already exists/i, key: 'apiErrors.duplicateUserEmail' },
  // PHX-1170: missing admin role on POST/PATCH/DELETE /onboarding/api/v1/users
  { pattern: /User creation\/update\/delete requires admin role/i, key: 'apiErrors.userManagementRequiresAdmin' },
  // Convert mother milk → donor milk validation (validateConvertBottle / bulkConvertMotherToDonor repo checks)
  { pattern: /^Only (MOTHER )?(bottles|receivables) can be converted( to DONOR)?\./i, key: 'inventory.convert.errorNotMotherBottle' },
  { pattern: /Bottle is expired and cannot be converted/i, key: 'inventory.convert.errorContainerExpired' },
  { pattern: /status must be RECEIVED for conversion|Only RECEIVED receivables can be converted/i, key: 'inventory.convert.errorStatusNotReceived' },
  { pattern: /person_type BOTH to be eligible for donation|person_type '?BOTH'? \(registered as both mother and donor\)/i, key: 'inventory.convert.errorNotADonor' },
  { pattern: /must have an APPROVED screening status|must be an APPROVED donor/i, key: 'inventory.convert.errorScreeningNotApproved' },
  { pattern: /already been used and cannot be converted/i, key: 'inventory.convert.errorAlreadyUsed' },
  // tms-api-grpc: download / storage errors (PrintAgent certificate downloads)
  { pattern: /AWS credentials unavailable or invalid/i,                    key: 'settings.printAgentSetup.awsCredentialsInvalid' },
  { pattern: /File not found in storage/i,                                 key: 'settings.printAgentSetup.fileNotFound' },
  { pattern: /Access denied to storage/i,                                  key: 'settings.printAgentSetup.storageAccessDenied' },
  { pattern: /Storage service unreachable/i,                               key: 'settings.printAgentSetup.storageUnreachable' },
  { pattern: /Failed to fetch from storage/i,                              key: 'settings.printAgentSetup.storageFetchFailed' },
  { pattern: /Unable to determine tenant from token/i,                     key: 'settings.printAgentSetup.tenantResolutionFailed' },
  // tms-api-grpc: printer / agent errors — reuse existing key for "No agents connected"
  { pattern: /No agents connected/i,                                       key: 'settings.printAgentSetup.connectionFailed' },
  { pattern: /Tenant could not be resolved from client certificate/i,      key: 'settings.printAgentSetup.tenantCertFailed' },
  // tms-api-grpc: BridgeLink TCP connection errors
  { pattern: /TCP connection closed before ACK/i,                          key: 'settings.printAgentSetup.tcpConnectionClosed' },
  { pattern: /TCP connection timed out/i,                                  key: 'settings.printAgentSetup.tcpConnectionTimeout' },
  // tms-api-reports: adverse events — patient/donor hospital membership validation
  { pattern: /Patient not found for this hospital/i,                       key: 'reports.incident.patientNotFound' },
  { pattern: /Donor not found for this hospital/i,                         key: 'reports.incident.donorNotFound' },
  // tms-api-reports: generic server error returned explicitly in response body
  { pattern: /^An unexpected error occurred$/i,                            key: 'apiErrors.serverError' },
  // tms-api-feed-mgmt: feed preparation validation errors surfaced via getApiErrorMessage()
  { pattern: /NPO orders cannot be prepared/i,                             key: 'feed.prepare.npoOrderCannotProceed' },
  { pattern: /Mother provider is discharged/i,                             key: 'feed.prepare.api.motherDischarged' },
  { pattern: /Donor provider is not approved for milk usage/i,             key: 'feed.prepare.api.donorNotApproved' },
  { pattern: /Only Mother'?s Own Milk is permitted for this order/i,       key: 'feed.prepare.motherMilkOnlyBlock' },
  { pattern: /No valid donor consent found/i,                              key: 'feed.prepare.api.donorConsentMissing' },
  { pattern: /No preferred donor is linked/i,                              key: 'feed.prepare.api.noPreferredDonor' },
  { pattern: /No donor identified from scanned container/i,                key: 'feed.prepare.api.donorNotIdentified' },
  { pattern: /Allergen conflict:/i,                                        key: 'feed.prepare.api.allergenConflict' },
  // tms-api-inventory lot-recalled must come before the generic /has been recalled/ below
  { pattern: /Receiving this stock is not permitted/i,                      key: 'inventory.errors.lotRecalled' },
  { pattern: /Batch .+ has been recalled/i,                                key: 'feed.prepare.api.batchRecalled' },
  { pattern: /has been recalled/i,                                         key: 'feed.prepare.api.productRecalled' },
  { pattern: /Milk container .+ has expired/i,                             key: 'feed.prepare.api.milkExpired' },
  { pattern: /was opened on .+ must be discarded after/i,                  key: 'feed.prepare.api.productOpenedExpired' },
  { pattern: /has expired \(expiry:/i,                                     key: 'feed.prepare.api.productExpired' },
  { pattern: /Used Quantity cannot exceed Available Quantity/i,            key: 'feed.prepare.api.quantityExceeded' },
  { pattern: /mL remaining.*specify remainder_action/i,                    key: 'feed.prepare.api.remainderActionRequired' },
  { pattern: /A wasted_reason is required/i,                               key: 'feed.prepare.api.wastedReasonRequired' },
  { pattern: /A storage_location_id is required/i,                         key: 'feed.prepare.api.storageLocationRequired' },
  { pattern: /milk_source_type is required for order base/i,               key: 'feed.prepare.api.milkSourceTypeRequired' },
  { pattern: /does not belong to this patient.?s own mother/i,             key: 'feed.prepare.api.momMilkMismatch' },
  // Create/Edit order: Mother's Milk Only requires a linked maternal record (feedOrder.service create())
  { pattern: /^Unable to place 'Mother.?s Milk Only' order: No maternal record is linked to this patient/i, key: 'feed.validation.momOnlyNoLinkedMother' },
  // tms-api-allergens: config not-found errors shown via AllergyManagementView.vue toasts
  { pattern: /No allergen config found/i,                                   key: 'apiErrors.recordNotFound' },
  // tms-api-feed-mgmt: GET /feedPrep/stored soft-stop warning (checkPendingSchedules) —
  // raw English text returned in data.warnings[].message on a 200 response, not an
  // i18n key, so it must be mapped here or it renders untranslated in non-English locales.
  { pattern: /already has feeds scheduled for a future time/i,              key: 'feed.prepare.pendingScheduleWarning' },
  // tms-api-facilities: facility, care unit and bed errors via HospitalConfigView.vue
  { pattern: /bed_capacity cannot be reduced/i,                              key: 'settings.hospitalConfig.bedCapacityCannotBeReduced' },
  { pattern: /Hospital not found:/i,                                         key: 'apiErrors.recordNotFound' },
  { pattern: /No care units found for facility/i,                            key: 'apiErrors.recordNotFound' },
  { pattern: /Beds with Care Unit ID/i,                                      key: 'apiErrors.recordNotFound' },
  // tms-api-inventory: lot and movement errors via UpdateStockPage and MoveInventoryDialog
  { pattern: /Cannot remove unit\(s\) that are currently OPENED/i,           key: 'inventory.errors.cannotRemoveOpenedUnits' },
  { pattern: /Cannot remove unit\(s\) that are already disposed/i,           key: 'inventory.errors.cannotRemoveDisposedUnits' },
  { pattern: /Destination location is inactive/i,                            key: 'inventory.errors.destinationInactive' },
  { pattern: /Destination location not found/i,                              key: 'apiErrors.recordNotFound' },
  // tms-api-milk-mgmt: batch and pool errors via milk-batches.store and milk-pools.store
  { pattern: /No expiry rule configured for the selected storage location/i,          key: 'milk.api.noExpiryRule' },
  { pattern: /receivables are already PROCESSED and cannot be added to a pool/i,     key: 'milk.api.receivablesAlreadyProcessed' },
  { pattern: /Milk bottles from different donors cannot be added to the same pool/i,  key: 'milk.api.mixedDonors' },
  { pattern: /All bottles in a pool must have the same milk type/i,                   key: 'milk.api.mixedMilkTypes' },
  { pattern: /Only pools with source_type.*can be used to create a batch/i,           key: 'milk.api.invalidPoolSourceType' },
  { pattern: /Requested volume.*exceeds available pool volume/i,                      key: 'milk.batchExceedsPoolVolume' },
  // tms-api-onboarding: rate-limiting and bulk user CSV upload errors
  { pattern: /Too many requests/i,                                                    key: 'apiErrors.tooManyRequests' },
  { pattern: /No file uploaded.*Provide a CSV|No file uploaded.*file.*field/i,        key: 'user.api.csvNoFile' },
  { pattern: /Only CSV files are accepted/i,                                          key: 'user.uploadCsvOnly' },
  { pattern: /Uploaded CSV file is empty/i,                                           key: 'user.csvNoDataRows' },
  // tms-api-patient: health screening and screening approval/rejection errors
  { pattern: /At least one section.*must be provided/i,                               key: 'patient.api.healthSectionRequired' },
  { pattern: /Screening approval is not applicable for MOTHER/i,                      key: 'patient.api.screeningApprovalNotApplicable' },
  { pattern: /Screening rejection is not applicable for MOTHER/i,                     key: 'patient.api.screeningRejectionNotApplicable' },
  // tms-api-patient: document management not-found errors via mothers.store
  // (fetchDocumentsByPerson / removeSupportingDocument / removeHealthRecordAttachment
  // -> mothersStore.documentsError, shown in ConsentDocumentsStep.vue). Raw English
  // text returned by document.service.ts, not an i18n key, so it must be mapped here
  // or it renders untranslated in non-English locales.
  { pattern: /Supporting document .+ not found/i,                                     key: 'apiErrors.recordNotFound' },
  { pattern: /Health record .+ not found/i,                                           key: 'apiErrors.recordNotFound' },
  { pattern: /Attachment .+ not found/i,                                              key: 'apiErrors.recordNotFound' },
  { pattern: /Consent record .+ not found/i,                                          key: 'apiErrors.recordNotFound' },
];

/**
 * Map HTTP status codes to i18n keys for errors that lack a server-provided message.
 */
const STATUS_CODE_MAP: Record<number, string> = {
  500: 'apiErrors.serverError',
  502: 'apiErrors.serverError',
  503: 'apiErrors.serviceUnavailable',
  429: 'apiErrors.tooManyRequests',
};

/**
 * Check if an error is a 404 Not Found response.
 */
export function isNotFoundError(err: unknown): boolean {
  return axios.isAxiosError(err) && err.response?.status === 404;
}

/**
 * Check if an error is a 404 that indicates a resource is not yet configured
 * (vs. a genuinely missing entity). This is a semantic alias for readability.
 */
export function isNotConfiguredError(err: unknown): boolean {
  return isNotFoundError(err);
}

/**
 * Regex matching UUIDs (hyphenated or non-hyphenated) that may appear in error messages.
 * Users don't need to see internal IDs like hospital_id or person_id.
 */
const UUID_PATTERN = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b|\b[0-9a-f]{32}\b/gi;

/**
 * Sanitize a technical error message into something a non-technical user can understand.
 * - Strips UUIDs that are meaningless to end users
 * - Replaces known technical codes with translated friendly text
 * - Cleans up leftover artifacts (double spaces, trailing prepositions)
 */
export function sanitizeMessage(message: string): string {
  if (!message) return message ?? '';

  for (const { pattern, key } of TECHNICAL_MESSAGE_MAP) {
    if (pattern.test(message)) {
      const translated = i18next.t(key);
      return translated ?? message;
    }
  }

  // Strip UUIDs and clean up surrounding text
  let cleaned = message.replace(UUID_PATTERN, '').trim();
  // Remove dangling prepositions/articles left after UUID removal (e.g. "for ", "with id ")
  cleaned = cleaned.replace(/\b(for|with id|id:|id)\s*$/i, '').trim();
  // Collapse multiple spaces
  cleaned = cleaned.replace(/\s{2,}/g, ' ').trim();
  // Remove trailing punctuation artifacts
  cleaned = cleaned.replace(/[:\s"']+$/, '').trim();

  return cleaned || message;
}

/**
 * Extract a user-friendly error message from an error.
 *
 * Priority:
 * 1. Server-provided message from the API response body (already enriched by interceptor)
 * 2. Sanitized technical message (if it contains codes like P2025, ECONNREFUSED)
 * 3. Provided fallback (i18n key — resolved via i18next.t)
 *
 * @param err - The caught error
 * @param fallback - i18n key or pre-translated string to use if nothing better is available
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  // Resolve fallback: if it looks like an i18n key (contains a dot, no spaces), translate it
  const resolvedFallback = fallback.includes('.') && !fallback.includes(' ')
    ? (i18next.t(fallback) ?? fallback)
    : fallback;

  let message = resolvedFallback;

  if (axios.isAxiosError(err)) {
    const serverMessage =
      err.response?.data?.error?.message ||
      err.response?.data?.message ||
      err.message;

    if (serverMessage && serverMessage !== 'Request failed with status code ' + err.response?.status) {
      message = serverMessage;
    } else if (err.response?.status && STATUS_CODE_MAP[err.response.status]) {
      // No meaningful server message — map the HTTP status code directly
      const translated = i18next.t(STATUS_CODE_MAP[err.response.status]);
      return translated ?? resolvedFallback;
    }
  } else if (err instanceof Error && err.message) {
    message = err.message;
  }

  return sanitizeMessage(message);
}

/**
 * Translated hard-stop message for a 409 raised when a bottle that belongs to
 * an active milk pool is used for feeding (`details.reason === 'BOTTLE_IN_POOL'`).
 * Returns null for any other error so callers can fall back to their usual message.
 */
export function getBottleInPoolMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return null;
  const details = err.response.data?.error?.details as
    | { reason?: string; receipt_number?: string; pool_number?: string }
    | undefined;
  if (details?.reason !== 'BOTTLE_IN_POOL') return null;
  return i18next.t('apiErrors.bottleInPool', {
    receipt: details.receipt_number ?? '',
    pool: details.pool_number ?? '',
  });
}

// Batch status → label key; mirrors `milkBatchStatus` in useEnumTranslation.
const MILK_BATCH_STATUS_KEYS: Record<string, string> = {
  Unapproved: 'milk.statusUnapproved',
  Approved:   'milk.actionApproved',
  Retest:     'milk.actionRetest',
  Failed:     'user.failed',
  Rejected:   'donor.rejected',
};

type MilkHardStopDetails = {
  reason?: string;
  receipt_number?: string;
  pool_number?: string;
  batch_number?: string;
  batch_status?: string | null;
  batches?: Array<{ batch_number: string; status: string | null }>;
  container_barcode?: string;
};

function batchStatusLabel(status: string | null | undefined): string {
  if (!status) return i18next.t('common.unknown');
  const key = MILK_BATCH_STATUS_KEYS[status];
  return key ? i18next.t(key) : status;
}

/**
 * Translated hard-stop message for the milk-safety 409s raised during feed
 * preparation, quick feed, administration, re-dosing and batch editing,
 * identified by `details.reason`:
 * BOTTLE_IN_POOL, BATCH_NOT_APPROVED, BATCH_HIERARCHY_CONFLICT,
 * DUPLICATE_MILK_ITEM and POOL_EMPTY_LOCKED.
 * Returns null for any other error so callers can fall back to their usual message.
 */
export function getMilkHardStopMessage(err: unknown): string | null {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return null;
  const details = err.response.data?.error?.details as MilkHardStopDetails | undefined;

  switch (details?.reason) {
    case 'BOTTLE_IN_POOL':
      return getBottleInPoolMessage(err);
    case 'BATCH_NOT_APPROVED': {
      const batches = details.batches?.length
        ? details.batches
        : [{ batch_number: details.batch_number ?? '', status: details.batch_status ?? null }];
      return i18next.t('apiErrors.batchNotApproved', {
        batches: batches.map((b) => `${b.batch_number} (${batchStatusLabel(b.status)})`).join(', '),
      });
    }
    case 'BATCH_HIERARCHY_CONFLICT':
      return i18next.t('apiErrors.batchHierarchyConflict', {
        bottle: details.container_barcode ?? '',
        batch: details.batch_number ?? '',
      });
    case 'DUPLICATE_MILK_ITEM':
      return i18next.t('apiErrors.duplicateMilkItem', {
        item: details.container_barcode ?? details.batch_number ?? '',
      });
    case 'POOL_EMPTY_LOCKED':
      return i18next.t('apiErrors.poolEmptyLocked', { batch: details.batch_number ?? '' });
    default:
      return null;
  }
}

/**
 * Handle a 404 error silently — returns true if the error was a 404 (caller should
 * treat as empty/not-configured), false if it's a different error that needs handling.
 *
 * Usage:
 * ```ts
 * } catch (err) {
 *   if (handleSilent404(err, 'Allergens')) return;
 *   this.error = getApiErrorMessage(err, 'Failed to load allergens');
 * }
 * ```
 */
export function handleSilent404(err: unknown, context: string): boolean {
  if (isNotFoundError(err)) {
    console.info(`[${context}] Not configured (404) — using empty defaults`);
    return true;
  }
  return false;
}

/**
 * Returns true when the error is a 409 CONFLICT that indicates a duplicate MRN.
 *
 * Covers two backend scenarios:
 *  1. Pre-flight assertMrnUnique threw with "MRN '...' is already in use" —
 *     the message itself contains "mrn" and "already in use".
 *  2. A P2002 unique-constraint violation bubbled up as the generic
 *     "Unique constraint violated on unknown field" — detected by
 *     HTTP 409 + CONFLICT code + prismaCode P2002 in the error details.
 *
 * Only use this check in create/register call-sites where the only realistic
 * unique-constraint conflict is MRN (patient, mother, donor registration).
 */
export function isDuplicateMrnError(err: unknown): boolean {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return false;
  const errorData = err.response?.data?.error as
    | { code?: string; message?: string; details?: { prismaCode?: string } }
    | undefined;
  if (!errorData) return false;
  const message = (errorData.message ?? '').toLowerCase();
  // Case 1: message explicitly mentions MRN
  if (/mrn.*already in use|already in use.*mrn|this mrn is already/i.test(message)) return true;
  // Case 2: generic P2002 CONFLICT during registration — MRN is the only unique field users supply
  return errorData.code === 'CONFLICT' && errorData.details?.prismaCode === 'P2002';
}

/**
 * Returns true when the error is a 409 CONFLICT specifically caused by a duplicate
 * inventory lot (same product + location + batch already exists for the hospital).
 *
 * NOTE: `POST /inventory/receive` can also return 409 for an *unrelated* reason —
 * the supplied batch/lot has an active recall (see AppError.conflict() in
 * inventory.service.ts `receiveInventory()`). Callers must not assume every 409
 * from this endpoint is a duplicate-lot error; use this guard to distinguish the
 * two cases and let the recall message pass through untouched via
 * getApiErrorMessage().
 */
export function isDuplicateInventoryLotError(err: unknown): boolean {
  if (!axios.isAxiosError(err) || err.response?.status !== 409) return false;
  const message = (err.response?.data?.error?.message ?? '').toLowerCase();
  return /inventory lot.*already exists/i.test(message);
}
